// Enhanced formatters for international addresses

/**
 * Country-specific formatting rules
 */
const COUNTRY_FORMATTING_RULES = {
  // For France, show country instead of region for major cities
  'FR': {
    showCountryForMajorCities: true,
    majorCities: ['Paris', 'Lyon', 'Marseille', 'Toulouse', 'Nice', 'Nantes', 'Strasbourg', 'Montpellier'],
    regionAbbreviations: {
      'IDF': 'Île-de-France',
      'PAC': 'Provence-Alpes-Côte d\'Azur',
      'OCC': 'Occitanie',
      'NAQ': 'Nouvelle-Aquitaine'
    }
  },
  
  // For UK, show country instead of region
  'GB': {
    showCountryForMajorCities: true,
    majorCities: ['London', 'Birmingham', 'Manchester', 'Glasgow', 'Liverpool', 'Leeds', 'Sheffield'],
    regionAbbreviations: {
      'ENG': 'England',
      'SCT': 'Scotland', 
      'WLS': 'Wales',
      'NIR': 'Northern Ireland'
    }
  },
  
  // For Germany
  'DE': {
    showCountryForMajorCities: true,
    majorCities: ['Berlin', 'Hamburg', 'Munich', 'Cologne', 'Frankfurt'],
    regionAbbreviations: {
      'BE': 'Berlin',
      'BY': 'Bavaria',
      'HH': 'Hamburg'
    }
  },

  // For Canada, keep province format
  'CA': {
    showCountryForMajorCities: false,
    majorCities: ['Toronto', 'Montreal', 'Vancouver', 'Calgary', 'Ottawa'],
    regionAbbreviations: {
      'ON': 'Ontario',
      'QC': 'Quebec',
      'BC': 'British Columbia'
    }
  }
};

/**
 * Enhanced city/country formatter that handles international addresses better
 */
export const internationalCityFormatter = (place) => {
  let city = '';
  let region = '';
  let regionShort = '';
  let country = '';
  let countryShort = '';

  // Extract address components
  place.address_components?.forEach(component => {
    const types = component.types;
    
    if (types.includes('locality') || types.includes('sublocality_level_1')) {
      city = city || component.long_name;
    } else if (types.includes('administrative_area_level_1')) {
      region = component.long_name;
      regionShort = component.short_name;
    } else if (types.includes('country')) {
      country = component.long_name;
      countryShort = component.short_name;
    }
  });

  // Get country-specific rules
  const rules = COUNTRY_FORMATTING_RULES[countryShort];
  
  if (rules) {
    // Check if this is a major city that should show country instead of region
    if (rules.showCountryForMajorCities && rules.majorCities.includes(city)) {
      return `${city}, ${country}`;
    }
    
    // Use full region name if available
    if (rules.regionAbbreviations[regionShort]) {
      region = rules.regionAbbreviations[regionShort];
    }
  }

  // For US and similar countries, use region/state
  if (['US', 'CA', 'AU'].includes(countryShort)) {
    return city && regionShort ? `${city}, ${regionShort}` : place.formatted_address;
  }
  
  // For other countries, prefer country over region
  if (city && country) {
    return `${city}, ${country}`;
  }
  
  // Fallback to region if no country
  if (city && region) {
    return `${city}, ${region}`;
  }
  
  return place.formatted_address;
};

/**
 * Smart international formatter that adapts based on user location and preferences
 */
export const smartInternationalFormatter = (userCountry = 'US') => {
  return (place) => {
    let city = '';
    let region = '';
    let regionShort = '';
    let country = '';
    let countryShort = '';

    place.address_components?.forEach(component => {
      const types = component.types;
      
      if (types.includes('locality') || types.includes('sublocality_level_1')) {
        city = city || component.long_name;
      } else if (types.includes('administrative_area_level_1')) {
        region = component.long_name;
        regionShort = component.short_name;
      } else if (types.includes('country')) {
        country = component.long_name;
        countryShort = component.short_name;
      }
    });

    // If same country as user, show city, region
    if (countryShort === userCountry) {
      return city && regionShort ? `${city}, ${regionShort}` : place.formatted_address;
    }
    
    // For international locations, show city, country
    return city && country ? `${city}, ${country}` : place.formatted_address;
  };
};

/**
 * City-only formatter (removes regions/states)
 */
export const cityOnlyFormatter = (place) => {
  let city = '';
  
  place.address_components?.forEach(component => {
    const types = component.types;
    
    if (types.includes('locality') || types.includes('sublocality_level_1')) {
      city = city || component.long_name;
    }
  });
  
  return city || place.name || place.formatted_address;
};

/**
 * Context-aware formatter for travel/shipping applications
 */
export const travelDestinationFormatter = (place) => {
  let city = '';
  let region = '';
  let regionShort = '';
  let country = '';
  let countryShort = '';

  place.address_components?.forEach(component => {
    const types = component.types;
    
    if (types.includes('locality') || types.includes('sublocality_level_1')) {
      city = city || component.long_name;
    } else if (types.includes('administrative_area_level_1')) {
      region = component.long_name;
      regionShort = component.short_name;
    } else if (types.includes('country')) {
      country = component.long_name;
      countryShort = component.short_name;
    }
  });

  // Major international destinations - show country
  const majorInternationalCities = [
    'Paris', 'London', 'Tokyo', 'Berlin', 'Rome', 'Madrid', 'Amsterdam',
    'Vienna', 'Prague', 'Barcelona', 'Istanbul', 'Dubai', 'Singapore',
    'Hong Kong', 'Sydney', 'Melbourne', 'Mumbai', 'Delhi'
  ];

  if (majorInternationalCities.includes(city)) {
    return `${city}, ${country}`;
  }

  // For US/Canada, show state/province
  if (['US', 'CA'].includes(countryShort)) {
    return city && regionShort ? `${city}, ${regionShort}` : place.formatted_address;
  }

  // For other locations, show country
  return city && country ? `${city}, ${country}` : place.formatted_address;
};

/**
 * Shipping-optimized formatter (focuses on administrative divisions)
 */
export const shippingFormatter = (place) => {
  let city = '';
  let region = '';
  let regionShort = '';
  let country = '';
  let countryShort = '';

  place.address_components?.forEach(component => {
    const types = component.types;
    
    if (types.includes('locality') || types.includes('sublocality_level_1')) {
      city = city || component.long_name;
    } else if (types.includes('administrative_area_level_1')) {
      region = component.long_name;
      regionShort = component.short_name;
    } else if (types.includes('country')) {
      country = component.long_name;
      countryShort = component.short_name;
    }
  });

  // Countries where we need state/province for shipping
  const stateRequiredCountries = ['US', 'CA', 'AU', 'BR', 'IN'];
  
  if (stateRequiredCountries.includes(countryShort)) {
    return city && regionShort ? `${city}, ${regionShort}` : place.formatted_address;
  }

  // For other countries, country is more important for shipping
  return city && country ? `${city}, ${country}` : place.formatted_address;
};

/**
 * Updated BUILT_IN_FORMATTERS with international support
 */
export const ENHANCED_BUILT_IN_FORMATTERS = {
  // Original formatters
  cityState: (place) => {
    let city = '', state = '';
    place.address_components?.forEach(component => {
      const types = component.types;
      if (types.includes('locality') || types.includes('sublocality_level_1')) {
        city = city || component.long_name;
      }
      if (types.includes('administrative_area_level_1')) {
        state = component.short_name;
      }
    });
    return city && state ? `${city}, ${state}` : place.formatted_address;
  },
  
  fullAddress: (place) => place.formatted_address,
  
  // Enhanced international formatters
  cityCountry: internationalCityFormatter,
  smartInternational: smartInternationalFormatter(),
  cityOnly: cityOnlyFormatter,
  travelDestination: travelDestinationFormatter,
  shipping: shippingFormatter,
  
  // Context-specific formatters
  cruise: (place) => {
    // For cruise destinations, prefer recognizable city/country format
    return travelDestinationFormatter(place);
  }
};

/**
 * Helper function to detect if a place needs special international formatting
 */
export const needsInternationalFormatting = (place) => {
  const countryCode = place.address_components?.find(component => 
    component.types.includes('country')
  )?.short_name;
  
  return countryCode && !['US', 'CA'].includes(countryCode);
};

/**
 * Configuration for different use cases
 */
export const INTERNATIONAL_CONFIGS = {
  // For cruise booking (international destinations common)
  cruise: {
    formatter: 'cruise',
    types: ['(cities)', 'establishment'],
    // No country restrictions for international cruises
  },
  
  // For US domestic shipping
  usDomestic: {
    formatter: 'cityState',
    types: ['(cities)'],
    componentRestrictions: { country: 'us' }
  },
  
  // For international shipping
  internationalShipping: {
    formatter: 'shipping',
    types: ['(cities)'],
    // No country restrictions
  },
  
  // For travel planning
  travel: {
    formatter: 'travelDestination', 
    types: ['(cities)', 'airport'],
    // No country restrictions
  }
};

export default ENHANCED_BUILT_IN_FORMATTERS;